import { FC, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModalProps } from './types';
import { ModalOverlay, ModalContainer, ModalHeader, ModalBody, CloseButton } from './styled';

export const Modal: FC<ModalProps> = ({
    isOpen,
    onRequestClose,
    header,
    children,
    size = 'default',
    zIndex,
}) => {
    const overlayRef = useRef<HTMLDivElement>(null);

    // Handle ESC key press
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onRequestClose();
            }
        },
        [onRequestClose]
    );

    // Handle click outside modal
    const handleOverlayClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget) {
                onRequestClose();
            }
        },
        [onRequestClose]
    );

    // Stop React-synthetic propagation for in-modal events. The native bubble
    // is handled by the document-level capture listener installed below.
    const handleModalPointerDown = useCallback((event: React.PointerEvent) => {
        event.stopPropagation();
    }, []);

    const handleContainerClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    // Prevent body scroll and add ESC listener when modal is open
    useEffect(() => {
        if (isOpen) {
            // Prevent body scroll
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';

            // Add ESC key listener
            document.addEventListener('keydown', handleKeyDown);

            return () => {
                // Restore body scroll
                document.body.style.overflow = originalStyle;
                // Remove ESC key listener
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isOpen, handleKeyDown]);

    // Swallow native pointer/mouse-down events that originate inside the modal
    // BEFORE they can reach outside-click detectors mounted on document
    // (Radix `DismissableLayer`, drag libraries, etc.). We must do this in
    // capture phase at document level so we run before Radix's bubble-phase
    // listener; React's synthetic stopPropagation alone doesn't reach it
    // because the modal is portaled to `document.body`, outside the React
    // root that React delegates from.
    useEffect(() => {
        if (!isOpen) return;
        const stopIfInside = (event: Event) => {
            const overlay = overlayRef.current;
            const target = event.target;
            if (overlay && target instanceof Element && overlay.contains(target)) {
                const isMonacoTarget = Boolean(target.closest('.monaco-editor'));
                if (isMonacoTarget) {
                    return;
                }
                event.stopPropagation();
            }
        };
        document.addEventListener('pointerdown', stopIfInside, true);
        document.addEventListener('mousedown', stopIfInside, true);
        return () => {
            document.removeEventListener('pointerdown', stopIfInside, true);
            document.removeEventListener('mousedown', stopIfInside, true);
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const modalContent = (
        <ModalOverlay
            ref={overlayRef}
            onClick={handleOverlayClick}
            onPointerDown={handleModalPointerDown}
            zIndex={zIndex}
        >
            <ModalContainer size={size} onClick={handleContainerClick}>
                <div style={{ position: 'relative' }}>
                    <CloseButton onClick={onRequestClose} aria-label="Close modal" type="button">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="20px"
                            viewBox="0 -960 960 960"
                            width="20px"
                            fill="currentColor"
                        >
                            <path d="m336-280-56-56 144-144-144-143 56-56 144 144 143-144 56 56-144 143 144 144-56 56-143-144-144 144Z" />
                        </svg>
                    </CloseButton>
                    <ModalHeader>{header}</ModalHeader>
                </div>
                <ModalBody>{children}</ModalBody>
            </ModalContainer>
        </ModalOverlay>
    );

    // Use portal to render modal at document.body level
    return createPortal(modalContent, document.body);
};

export default Modal;
